import type {
  ComponentLineItem,
  LineItem
} from '@simplisafe/eis-commercetools-carts'
import type { EcommEventWithCartId, ProductPayload } from './types'

import type { CartValue } from '@ecomm/data-simplisafe-api'
import { getCartId } from '@ecomm/data-storage'
import { getCurrencyFromLocale } from '@ecomm/utils/locale/currencyCode'
import { getLocale } from '@ecomm/utils/locale/getLocale'
import type { LocaleCodeType } from '@simplisafe/eis-commercetools-shared'

type QuantityType = 'multiple' | 'single'

// @TODO: Replace with import from eis-commercetools!
export const getDiscountedPerUnitPrice = (
  lineItem: LineItem,
  quantity: number,
  locale: LocaleCodeType
) => {
  if (!quantity) {
    return 0
  }

  return (
    (lineItem.pricesAfterDiscount[locale]?.centAmount || 0) / (100 * quantity)
  )
}

/**
 * Converts an individual LineItem (can be a package) into a data structure that we can send to
 * Rudderstack.
 * The quantityType prop is used to distinguish single increment/decrement from bulk line item
 * deletion. It will allow us to track the proper quantity and price total.
 */
export const lineItemToRudderstackData = (
  lineItem: LineItem,
  cart: CartValue,
  quantityType: QuantityType = 'single'
): EcommEventWithCartId => {
  const locale = getLocale()
  const currency = getCurrencyFromLocale(locale)
  const quantityDivisor = quantityType === 'single' ? lineItem.quantity || 1 : 1
  const total = getDiscountedPerUnitPrice(lineItem, quantityDivisor, locale)
  const products = ctLineItemToRudderstackProducts(lineItem, quantityType)
  const eventData = {
    cartId: getCartId() || '',
    coupon: cart.discountCode || '',
    currency,
    products,
    total
  }

  return eventData
}

/**
 * Turns a list of Package LineItem components into a Rudderstack data structure
 * The quantityType prop is used to distinguish single increment/decrement from bulk line item
 * deletion. It will allow us to track the proper quantity and price total.
 */
const packageItemsToRudderstackData = ({
  packageItems,
  packageQuantity,
  quantityType = 'single',
  packageParentId
}: {
  readonly packageItems: readonly ComponentLineItem[]
  readonly packageQuantity: number
  readonly quantityType: QuantityType
  readonly packageParentId?: string
}): readonly ProductPayload[] => {
  const locale = getLocale()
  const quantityDivisor = quantityType === 'single' ? packageQuantity || 1 : 1

  const items = packageItems.map(packageItem => ({
    category: packageItem.productType,
    item_list_id: packageParentId || '',
    name: packageItem.name[locale],
    price: 0,
    product_id: packageItem.sku,
    quantity: packageItem.quantity / quantityDivisor
  }))
  return items
}

/**
 * Converts an individual LineItem (can be a package) into a data structure to be used in the
 * products array of the Rudderstack data object
 * The quantityType prop is used to distinguish single increment/decrement from bulk line item
 * deletion. It will allow us to track the proper quantity and price total.
 */
export const ctLineItemToRudderstackProducts = (
  lineItem: LineItem,
  quantityType: QuantityType = 'single'
): readonly ProductPayload[] => {
  const locale = getLocale()
  const quantityDivisor = quantityType === 'single' ? lineItem.quantity || 1 : 1
  const quantity = quantityType === 'single' ? 1 : lineItem.quantity
  const price = getDiscountedPerUnitPrice(lineItem, quantityDivisor, locale)

  if (lineItem.productType === 'package_parent') {
    return [
      {
        category: lineItem.productType,
        item_list_id: lineItem.packageParentId,
        name: lineItem.name[locale],
        price,
        product_id: lineItem.sku || '',
        quantity
      },
      ...packageItemsToRudderstackData({
        packageItems: lineItem.packageItems,
        packageQuantity: lineItem.quantity,
        quantityType,
        packageParentId: lineItem.packageParentId
      })
    ]
  } else {
    return [
      {
        category: lineItem.productType,
        item_list_id: lineItem.packageParentId || '',
        name: lineItem.name[locale],
        price,
        product_id: lineItem.sku || '',
        quantity
      }
    ]
  }
}
